import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { Resolver, useForm } from "react-hook-form";
import { useParams } from "react-router";
import { z } from "zod";
import { useRfpCoverages } from "@platform/api";
import { Option } from "@platform/design-system";

import { UsePatchFormDraftMutationResponse } from "../../../../hooks/usePatchFormDraftMutation";
import { useRfpClientDetails } from "../../../../hooks/useRfpClientDetails";
import { useRfpDetails } from "../../../../hooks/useRfpDetails";
import {
  ClientProductCarrierWithCarrier,
  Coverage,
  RfpCoveragesResponse,
} from "../../../../types/rfpApi";
import { isClientProspect } from "../../../../utils/client";
import {
  formatDateToStartOfDay,
  formatResponseDate,
} from "../../../../utils/common";

import { FORM_FIELDS } from "./constants";
import { ContractedCarrier } from "./ContractedCarrier";
import {
  getFilteredProductOptions,
  getFinalDecisionFormFields,
  initializeRenewalDates,
  updateIncumbentProductFields,
} from "./getFinalDecisionFormFields";
import { getReviewData, hasAnyMissingFields } from "./getReviewData";
import { IncumbentData } from "./IncumbentData";
import { LastPayment } from "./LastPayment";
import {
  CoverageFormValues,
  FinalDecisionFormGeneratorProps,
  FinalDecisionFormValues,
  FinalDecisionsWithSubmittedForm,
  ReviewDataResponse,
} from "./types";
import { useFinalDecisionQuery } from "./useFinalDecisionApi";
import { useGetCarriers } from "./useGetCarriers";
import { useGetProducts } from "./useGetProducts";

const stringNumberSchema = z
  .string()
  .nullish()
  .optional()
  .refine(
    (value) => {
      if (!value || value.trim() === "") {
        return true; // Allow empty/undefined
      }
      const numericValue = Number(value);
      return !isNaN(numericValue) && numericValue >= 0;
    },
    {
      message: "Must be a valid positive number",
    },
  );
const stringIntegerSchema = (
  min: number = 1,
  max: number = 99,
): z.ZodEffects<
  z.ZodOptional<z.ZodOptional<z.ZodNullable<z.ZodString>>>,
  string,
  string
> =>
  z
    .string()
    .nullish()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === "") {
          return true; // Allow empty/undefined
        }
        const numericValue = Number(value);
        return (
          !isNaN(numericValue) &&
          Number.isInteger(numericValue) &&
          numericValue >= min &&
          numericValue <= max
        );
      },
      {
        message: `Must be an integer between ${min} and ${max}`,
      },
    );

const stringPercentageSchema = z
  .string()
  .nullish()
  .optional()
  .refine(
    (value) => {
      if (!value || value.trim() === "") {
        return true; // Allow empty/undefined
      }
      const numericValue = Number(value);
      return !isNaN(numericValue) && numericValue >= 0 && numericValue < 100;
    },
    {
      message: "Must be a percentage between 0 and 99",
    },
  );

export const finalDecisionFormValuesSchema = z
  .object({
    currentCoverages: z.array(
      z
        .object({
          incumbentAnnualPremium: stringNumberSchema,
          annualWrittenPremium: stringNumberSchema,
          numberOfPlansQuoted: stringIntegerSchema(1, 99),
          rateGuarantee: stringIntegerSchema(0, 9),
          winningGaOverridePercentage: stringPercentageSchema,
          incumbentGaOverridePercentage: stringPercentageSchema,
        })
        .partial()
        .passthrough(),
    ),
    newCoverages: z.array(
      z
        .object({
          annualWrittenPremium: stringNumberSchema,
          numberOfPlansQuoted: stringIntegerSchema(1, 99),
          rateGuarantee: stringIntegerSchema(0, 9),
          winningGaOverridePercentage: stringPercentageSchema,
        })
        .partial()
        .passthrough(),
    ),
  })
  .partial()
  .passthrough();

export type UseFinalDecisionFormReturn = Pick<
  FinalDecisionFormGeneratorProps,
  "fields" | "formMethods" | "components"
> & {
  isFormDataLoading: boolean;
  finalDecisionId?: number;
  rfpId?: string;
  onSaveDraft: () => Promise<boolean>;
  getReviewData: () => ReviewDataResponse;
  hasAnyMissingFields: () => boolean;
  lastDraftSavedAt?: string;
  hasChangesFromSubmitted: () => boolean;
};

interface UseFinalDecisionFormOptions {
  pathDraftMutation: UsePatchFormDraftMutationResponse<unknown>;
}

// Reorder RFP coverages to match the order they were saved in the draft, with
// any RFP coverages that have no saved entry appended to the end. Coverage
// position on the form is normally driven by `productorder.sortorder`, which is
// global and not RFP-scoped — so a new rfpproduct can land anywhere in the list
// and visually shift every other row. Once a draft has been saved, the order
// the analyst saw is recorded by the index of each entry in the saved
// `currentCoverages` / `newCoverages` arrays. Re-rendering in that saved order
// keeps the analyst's mental model intact and prevents new coverages from
// being inserted in the middle of an in-progress form.
export const reorderRfpCoveragesBySaved = (
  savedCoverages: { rfpProductId?: number }[] | undefined,
  rfpCoverages: Coverage[] | null | undefined,
): Coverage[] => {
  if (!rfpCoverages?.length) {
    return [];
  }
  if (!savedCoverages?.length) {
    return rfpCoverages;
  }

  const rfpById = new Map(rfpCoverages.map((cov) => [cov.id, cov]));
  const seen = new Set<number>();
  const ordered: Coverage[] = [];

  savedCoverages.forEach((saved) => {
    const id = saved.rfpProductId;
    if (id === undefined || seen.has(id)) {
      return;
    }
    const match = rfpById.get(id);
    if (match) {
      ordered.push(match);
      seen.add(id);
    }
  });

  rfpCoverages.forEach((cov) => {
    if (!seen.has(cov.id)) {
      ordered.push(cov);
    }
  });

  return ordered;
};

// Sync saved coverages with current RFP coverages
export const autoCompleteIncumbentProducts = (
  coverages: CoverageFormValues[],
  rfpCoverages: Coverage[],
  productMap: Map<number, ClientProductCarrierWithCarrier>,
  products: Option[],
): CoverageFormValues[] | null => {
  let hasChanges = false;
  const updatedCoverages = coverages.map((coverage, index) => {
    const rfpCoverage = rfpCoverages[index];
    if (!rfpCoverage || coverage.incumbentProductId) {
      return coverage;
    }

    const filteredOptions = getFilteredProductOptions(
      rfpCoverage,
      productMap,
      products,
    );
    if (filteredOptions.length !== 1) {
      return coverage;
    }

    hasChanges = true;
    const updatedCoverage = { ...coverage };
    updateIncumbentProductFields(
      updatedCoverage,
      filteredOptions[0].value as number,
      productMap,
    );
    return updatedCoverage;
  });

  return hasChanges ? updatedCoverages : null;
};

export const syncCoveragesWithRfp = <
  T extends { rfpProductId?: number; sold?: boolean | null },
>(
  savedCoverages: T[] | undefined,
  rfpCoverages: Coverage[] | undefined,
): (T | { rfpProductId: number })[] => {
  if (!rfpCoverages?.length) {
    return [];
  }

  // Create map of saved coverages by rfpProductId for quick lookup
  const savedCoveragesMap = new Map<number, T>();
  savedCoverages?.forEach((coverage) => {
    if (coverage.rfpProductId) {
      savedCoveragesMap.set(coverage.rfpProductId, coverage);
    }
  });

  // For each RFP coverage, use saved data if exists, otherwise initialize
  return rfpCoverages.map((rfpCov) => {
    const savedCoverage = savedCoveragesMap.get(rfpCov.id);
    if (savedCoverage) {
      // Format renewalDate if it exists
      const renewalDate =
        FORM_FIELDS.RENEWAL_DATE in savedCoverage
          ? formatResponseDate(savedCoverage.renewalDate as string | null)
          : undefined;

      return {
        ...savedCoverage,
        ...(renewalDate !== undefined && { renewalDate }),
        sold: savedCoverage?.sold ?? false,
      };
    }

    return {
      rfpProductId: rfpCov.id,
    };
  });
};

export const buildFormValuesWithExternalIds = (
  finalDecision: FinalDecisionsWithSubmittedForm | undefined,
  rfpCoverages: RfpCoveragesResponse | undefined,
): FinalDecisionFormValues => {
  // We need rfpCoverages to build the form values, so return empty object if not present
  if (!rfpCoverages) {
    return {
      currentCoverages: [],
      newCoverages: [],
    };
  }

  // Sync saved form data with current RFP coverages
  const syncedCurrentCoverages = syncCoveragesWithRfp(
    finalDecision?.formData?.currentCoverages,
    rfpCoverages.currentCoverages,
  );
  const syncedNewCoverages = syncCoveragesWithRfp(
    finalDecision?.formData?.newCoverages,
    rfpCoverages.newCoverages,
  );

  return {
    ...finalDecision?.formData,
    currentCoverages: syncedCurrentCoverages,
    newCoverages: syncedNewCoverages,
  };
};

// Normalize value for comparison: remove formatting, treat null/undefined/empty string as equivalent
const normalizeValue = (value: unknown): unknown => {
  // Treat null, undefined, and empty string as equivalent (null)
  if (value === undefined || value === null || value === "") {
    return null;
  }

  // Normalize Date objects to ISO string for comparison
  if (value instanceof Date) {
    return value.toISOString();
  }

  // Normalize numeric strings by converting to number (removes formatting)
  if (typeof value === "string") {
    const trimmed = value.trim();
    const asNumber = Number(trimmed);
    if (!isNaN(asNumber) && trimmed !== "") {
      return asNumber;
    }
    return trimmed;
  }

  // Arrays: normalize each element
  if (Array.isArray(value)) {
    return value.map(normalizeValue);
  }

  // Objects: normalize each property
  if (typeof value === "object" && value !== null) {
    const normalized: Record<string, unknown> = {};
    Object.keys(value).forEach((key) => {
      normalized[key] = normalizeValue((value as Record<string, unknown>)[key]);
    });
    return normalized;
  }

  return value;
};

// Internal fields that should not be compared (for UI/UX purposes only)
export const INTERNAL_FIELDS = new Set([
  "incumbentCarrierId",
  "lastEffectiveDate",
  "lastRateGuarantee",
  "renewalDateManualEntry",
  "lostBrokerOfRecord",
  "incumbentData",
  "winningMedicalCarrier",
  "incumbentMedicalCarrier",
  "sfExternalId",
  "incumbentProductIdManualEntry",
]);

// Check if a field should be ignored in comparison
const shouldIgnoreField = (fieldName: string): boolean =>
  INTERNAL_FIELDS.has(fieldName);

// Deep comparison of form values with normalization
const areFormValuesEqual = (value1: unknown, value2: unknown): boolean => {
  const normalized1 = normalizeValue(value1);
  const normalized2 = normalizeValue(value2);

  // Both null/undefined/empty
  if (normalized1 === null && normalized2 === null) {
    return true;
  }

  // One is null, the other is not
  if (normalized1 === null || normalized2 === null) {
    return false;
  }

  // Array comparison
  if (Array.isArray(normalized1) && Array.isArray(normalized2)) {
    if (normalized1.length !== normalized2.length) {
      return false;
    }
    return normalized1.every((item, index) =>
      areFormValuesEqual(item, normalized2[index]),
    );
  }

  // Object comparison
  if (
    typeof normalized1 === "object" &&
    typeof normalized2 === "object" &&
    !Array.isArray(normalized1) &&
    !Array.isArray(normalized2)
  ) {
    const keys1 = Object.keys(normalized1 as Record<string, unknown>);
    const keys2 = Object.keys(normalized2 as Record<string, unknown>);
    const allKeys = new Set([...keys1, ...keys2]);

    return Array.from(allKeys).every((key) => {
      // Skip internal fields
      if (shouldIgnoreField(key)) {
        return true;
      }
      return areFormValuesEqual(
        (normalized1 as Record<string, unknown>)[key],
        (normalized2 as Record<string, unknown>)[key],
      );
    });
  }

  // Primitive comparison
  return normalized1 === normalized2;
};

export const useFinalDecisionForm = (
  options: UseFinalDecisionFormOptions,
): UseFinalDecisionFormReturn => {
  const { id: rfpId } = useParams<{ id: string }>();
  const { data: clientDetails } = useRfpClientDetails({ rfpId });
  const { data: rfpCoverages } = useRfpCoverages({ rfpId });
  const { data: rfpDetails } = useRfpDetails({ rfpId });
  const { carrierMap, carriers } = useGetCarriers(rfpId);
  const { products, productMap } = useGetProducts(rfpId);
  const finalDecisionQuery = useFinalDecisionQuery({ rfpId });

  const isFormDataLoading = finalDecisionQuery.isLoading;
  const isProspect = isClientProspect(clientDetails?.type);

  // Once the analyst has saved a draft, freeze the coverage display order to
  // match the saved order. New rfpproducts added after that point are appended
  // at the end rather than being inserted in the middle by `productorder.sortorder`.
  const orderedRfpCoverages = useMemo<RfpCoveragesResponse | undefined>(() => {
    if (!rfpCoverages) {
      return undefined;
    }
    const savedFormData = finalDecisionQuery.data?.formData;
    return {
      ...rfpCoverages,
      currentCoverages: reorderRfpCoveragesBySaved(
        savedFormData?.currentCoverages,
        rfpCoverages.currentCoverages,
      ),
      newCoverages: reorderRfpCoveragesBySaved(
        savedFormData?.newCoverages,
        rfpCoverages.newCoverages,
      ),
    };
  }, [rfpCoverages, finalDecisionQuery.data?.formData]);

  const submittedFormStateRef = useRef<FinalDecisionFormValues | null>(null);

  const formMethods = useForm<FinalDecisionFormValues>({
    defaultValues: useMemo(
      () =>
        buildFormValuesWithExternalIds(
          finalDecisionQuery.data,
          orderedRfpCoverages,
        ),
      [finalDecisionQuery.data, orderedRfpCoverages],
    ),
    resolver: zodResolver(
      finalDecisionFormValuesSchema,
    ) as unknown as Resolver<FinalDecisionFormValues>,
    mode: "onBlur",
  });
  const reset = formMethods.reset;
  const getValues = formMethods.getValues;
  const isFormValid = formMethods.formState.isValid;

  const draftId = finalDecisionQuery.data?.draftId;
  const pathDraftMutateAsync = options.pathDraftMutation.mutateAsync;
  const handleSaveDraft = useCallback(() => {
    if (!draftId) {
      return Promise.reject(new Error("Draft id is missing"));
    }

    if (!isFormValid) {
      return Promise.resolve(false);
    }

    const formData = getValues();

    // Transform renewal dates to start of day format
    const transformedData = {
      ...formData,
      currentCoverages: formData.currentCoverages?.map((coverage) => ({
        ...coverage,
        renewalDate: formatDateToStartOfDay(coverage.renewalDate ?? null),
      })),
      newCoverages: formData.newCoverages?.map((coverage) => ({
        ...coverage,
        renewalDate: formatDateToStartOfDay(coverage.renewalDate ?? null),
      })),
    };

    return pathDraftMutateAsync({ draftId, formData: transformedData }).then(
      () => {
        reset(formData);
        return true;
      },
    );
  }, [pathDraftMutateAsync, draftId, getValues, reset, isFormValid]);

  // Init form values from FinalDecision and RFP Coverage APIs
  const initFormRef = useRef(true);
  useEffect(() => {
    const hasRequiredData = finalDecisionQuery.data && orderedRfpCoverages;
    if (hasRequiredData && initFormRef.current) {
      initFormRef.current = false;
      const enrichedFormData = buildFormValuesWithExternalIds(
        finalDecisionQuery.data,
        orderedRfpCoverages,
      );
      reset(enrichedFormData);

      // Store submitted form values for comparison (already transformed by finalDecisionToFormValues)
      submittedFormStateRef.current =
        finalDecisionQuery.data?.submittedFormValues ?? null;

      // Initialize renewal dates after form reset
      initializeRenewalDates(
        formMethods,
        rfpDetails?.revisedEffectiveDate,
        rfpDetails?.originalEffectiveDate,
      );
    }
  }, [
    finalDecisionQuery.data,
    orderedRfpCoverages,
    rfpDetails?.revisedEffectiveDate,
    rfpDetails?.originalEffectiveDate,
    reset,
    formMethods,
  ]);

  // Auto-complete incumbent product when only one option is available
  const autoCompleteExecutedRef = useRef(false);
  useEffect(() => {
    const hasRequiredData =
      orderedRfpCoverages?.currentCoverages &&
      products.length > 0 &&
      productMap.size > 0 &&
      !autoCompleteExecutedRef.current;

    if (!hasRequiredData) {
      return;
    }

    autoCompleteExecutedRef.current = true;
    const currentCoveragesValues =
      (formMethods.getValues("currentCoverages") as CoverageFormValues[]) || [];

    const updatedCoverages = autoCompleteIncumbentProducts(
      currentCoveragesValues,
      orderedRfpCoverages.currentCoverages,
      productMap,
      products,
    );

    if (updatedCoverages) {
      formMethods.setValue("currentCoverages", updatedCoverages, {
        shouldValidate: false,
      });
    }
  }, [
    orderedRfpCoverages,
    products,
    productMap,
    formMethods,
    rfpDetails?.revisedEffectiveDate,
    rfpDetails?.originalEffectiveDate,
  ]);

  const fields = useMemo(
    () =>
      getFinalDecisionFormFields({
        isProspect,
        rfpCoverages: orderedRfpCoverages,
        products,
        productMap,
        carriers,
        carrierMap,
        formMethods,
        onNext: handleSaveDraft,
        revisedEffectiveDate: rfpDetails?.revisedEffectiveDate,
        originalEffectiveDate: rfpDetails?.originalEffectiveDate,
      }),
    [
      isProspect,
      orderedRfpCoverages,
      products,
      productMap,
      carriers,
      carrierMap,
      handleSaveDraft,
      formMethods,
      rfpDetails?.revisedEffectiveDate,
      rfpDetails?.originalEffectiveDate,
    ],
  );

  const handleGetReviewData = useCallback(() => {
    const formValues = getValues();
    return getReviewData({
      formValues,
      carrierMap,
      productMap,
      rfpCoverages: orderedRfpCoverages,
    });
  }, [getValues, productMap, carrierMap, orderedRfpCoverages]);

  const handleHasAnyMissingFields = useCallback(() => {
    const reviewData = handleGetReviewData();
    return hasAnyMissingFields(reviewData);
  }, [handleGetReviewData]);

  /**
   * Checks if the current form values differ from submitted values.
   * This comparison is performed on-demand when called by the consuming component.
   * Returns true if there are no submitted values to compare against.
   */
  const handleHasChangesFromSubmitted = useCallback(() => {
    if (!submittedFormStateRef.current) {
      return true;
    }

    const currentFormValues = getValues();
    return !areFormValuesEqual(
      currentFormValues,
      submittedFormStateRef.current,
    );
  }, [getValues]);

  const components = {
    incumbentData: (props) => (
      <IncumbentData {...props} productMap={productMap} />
    ),
    contractedCarrier: (props) => <ContractedCarrier {...props} />,
    lastPayment: (props) => <LastPayment {...props} productMap={productMap} />,
  };

  const finalDecisionId = finalDecisionQuery.data?.finalDecisionId;
  const lastDraftSavedAt = finalDecisionQuery.data?.lastDraftSavedAt;
  return {
    formMethods,
    fields,
    components,
    isFormDataLoading,
    getReviewData: handleGetReviewData,
    hasAnyMissingFields: handleHasAnyMissingFields,
    onSaveDraft: handleSaveDraft,
    finalDecisionId,
    rfpId,
    lastDraftSavedAt,
    hasChangesFromSubmitted: handleHasChangesFromSubmitted,
  };
};
